import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { useCreateQuoteContactLead } from "../../queries/useCreateQuoteContactLead";
import { useAuth } from "../../stores/hooks/useAuth";
import { useCartName } from "../../stores/hooks/useCartName";
import { encodeTokenToHash } from "../../util/encodeTokenToHash";
import { getPdfDownloadLink } from "../../util/getPdfDownloadLink";
import { useOrderDetails } from "../Cart/useOrderDetails";
import { LeadFormButtons } from "./LeadFormButtons";
import {
  LeadFormModal,
  cartActionDownload,
  cartActionEmail,
  cartActionPrint,
  cartActionSaveQuote,
  useLeadFormModalState,
} from "./LeadFormModal";

const getActionPath = ({
  action,
  isOrder,
  orderNumber,
}: {
  action: "download" | "print";
  isOrder: boolean;
  orderNumber: string;
}): string => {
  const path = `${getDealerBasePath()}/${
    isOrder ? "order-details" : "wishlist"
  }/${action}/${orderNumber}`;

  return path;
};

export const LeadFormCart: FunctionComponent = () => {
  const downloadState = useLeadFormModalState("#download");
  const emailState = useLeadFormModalState("#email");
  const printState = useLeadFormModalState("#print");
  const saveQuoteState = useLeadFormModalState("#save-quote");
  const [token] = useAuth();
  const { t } = useTranslation();

  const createQuoteContactLead = useCreateQuoteContactLead();
  const { data: cart, isOrder } = useOrderDetails();
  const cartName = useCartName(cart);

  const downloadRedirectActon = (): void => {
    const link = getPdfDownloadLink({
      filename: `${cartName}.pdf`,
      orientation: "portrait",
      path: getActionPath({
        action: "download",
        isOrder,
        orderNumber: cart?.orderNumber ?? "",
      }),
      sso: encodeTokenToHash(token),
    });
    window.location.assign(link);
  };

  const printRedirectActon = (): void => {
    window.open(
      getActionPath({
        action: "print",
        isOrder,
        orderNumber: cart?.orderNumber ?? "",
      }),
      cartName
    );
  };

  return (
    <>
      <LeadFormButtons />
      <LeadFormModal
        generatedByPrefix=""
        hook={emailState}
        redirectAction={null}
        submitAction={createQuoteContactLead}
        subtitle={t(`leadForm.${isOrder ? "order" : "quote"}.subtitle.email`)}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.email`)}
        type={cartActionEmail}
      />
      <LeadFormModal
        subtitle={t(
          `leadForm.${isOrder ? "order" : "quote"}.subtitle.download`
        )}
        generatedByPrefix=""
        hook={downloadState}
        redirectAction={downloadRedirectActon}
        submitAction={createQuoteContactLead}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.download`)}
        type={cartActionDownload}
      />
      <LeadFormModal
        generatedByPrefix=""
        hook={printState}
        redirectAction={printRedirectActon}
        submitAction={createQuoteContactLead}
        subtitle={t(`leadForm.${isOrder ? "order" : "quote"}.subtitle.print`)}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.print`)}
        type={cartActionPrint}
      />
      <LeadFormModal
        generatedByPrefix=""
        hook={saveQuoteState}
        redirectAction={null}
        submitAction={createQuoteContactLead}
        subtitle={null}
        title={t(`leadForm.${isOrder ? "order" : "quote"}.title.save`)}
        type={cartActionSaveQuote}
      />
    </>
  );
};
